import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuthToken, getUserEmail } from '../utils/auth';
import { initializeGoogleIdentityServices } from '../utils/googleAuth';

interface AuthContextType {
  isAuthenticated: boolean;
  userEmail: string | null;
  signIn: () => void;
  signOut: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGoogleReady, setIsGoogleReady] = useState(false);

  const checkAuth = useCallback(() => {
    const token = getAuthToken();
    if (token) {
      const email = getUserEmail();
      setIsAuthenticated(true);
      setUserEmail(email);
    } else {
      setIsAuthenticated(false);
      setUserEmail(null);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    // Initialize Google Identity Services
    initializeGoogleIdentityServices();
    
    // Wait for Google to be ready
    const checkGoogle = setInterval(() => {
      // @ts-ignore
      if (window?.google?.accounts?.id) {
        clearInterval(checkGoogle);
        
        // @ts-ignore
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          callback: (response: any) => {
            localStorage.setItem('google_token', response.credential);
            checkAuth();
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        
        setIsGoogleReady(true);
      }
    }, 100);
    
    // Check initial auth state
    checkAuth();
    
    // Listen for auth expiration events
    const handleAuthExpired = () => {
      setIsAuthenticated(false);
      setUserEmail(null);
    };
    
    window.addEventListener('auth:expired', handleAuthExpired);
    
    return () => {
      clearInterval(checkGoogle);
      window.removeEventListener('auth:expired', handleAuthExpired);
    };
  }, [checkAuth]);

  const signIn = useCallback(() => {
    // @ts-ignore
    if (window?.google?.accounts?.id && isGoogleReady) {
      // Create a container for the button
      const buttonDiv = document.createElement('div');
      buttonDiv.id = 'google-signin-fallback';
      buttonDiv.style.position = 'fixed';
      buttonDiv.style.top = '-9999px';
      buttonDiv.style.left = '-9999px';
      buttonDiv.style.visibility = 'hidden';
      document.body.appendChild(buttonDiv);
      
      // @ts-ignore
      window.google.accounts.id.renderButton(
        buttonDiv,
        { 
          theme: 'filled_blue', 
          size: 'large',
          type: 'standard',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 250,
          click_listener: () => {
            // Clean up immediately after click
            setTimeout(() => {
              if (document.body.contains(buttonDiv)) {
                document.body.removeChild(buttonDiv);
              }
            }, 100);
          }
        }
      );
      
      // Wait for the button to be fully rendered before clicking
      setTimeout(() => {
        const button = buttonDiv.querySelector('div[role="button"]') as HTMLElement;
        if (button) {
          button.click();
        } else {
          // Fallback: if button not found, try the prompt method
          // @ts-ignore
          window.google.accounts.id.prompt();
        }
      }, 50);
    }
  }, [isGoogleReady, checkAuth]);

  const signOut = useCallback(() => {
    localStorage.removeItem('google_token');
    setIsAuthenticated(false);
    setUserEmail(null);
    
    // @ts-ignore
    if (window?.google?.accounts?.id) {
      // @ts-ignore
      window.google.accounts.id.disableAutoSelect();
    }
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, userEmail, signIn, signOut, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};